<template>
  <div class="chain-diagram">
    <svg
      v-if="supported"
      :viewBox="viewBox"
      class="chain-svg"
      preserveAspectRatio="xMidYMid meet"
      xmlns="http://www.w3.org/2000/svg"
    >
      <defs>
        <marker
          v-for="m in markerDefs"
          :id="m.id"
          :key="m.id"
          viewBox="0 0 10 10"
          refX="9"
          refY="5"
          markerUnits="userSpaceOnUse"
          markerWidth="12"
          markerHeight="12"
          orient="auto"
        >
          <path d="M 0 0 L 10 5 L 0 10 z" :fill="m.color" />
        </marker>
      </defs>

      <g v-for="box in boxes" :key="'box-' + box.id">
        <rect
          :x="box.x"
          :y="box.y"
          :width="box.w"
          :height="box.h"
          rx="8"
          ry="8"
          class="chain-box"
        />
        <foreignObject :x="box.x" :y="box.y" :width="box.w" :height="box.h">
          <div xmlns="http://www.w3.org/1999/xhtml" class="chain-box-inner">
            <span class="chain-box-label">{{ box.label }}</span>
          </div>
        </foreignObject>
      </g>

      <line
        v-for="(arr, i) in arrows"
        :key="'arr-' + i"
        :x1="arr.x1"
        :y1="arr.y1"
        :x2="arr.x2"
        :y2="arr.y2"
        :stroke="arr.color"
        stroke-width="3"
        :marker-end="'url(#' + arr.markerId + ')'"
      />
    </svg>

    <div v-else class="chain-diagram__fallback">
      <p>{{ sizeLabel(chain.swap_size) }} ({{ chain.ad_ids.length }} bostäder)</p>
    </div>
  </div>
</template>

<script>
import { swapSizeLabel } from '@/utils/swapMeta'

const COLORS = {
  yes: '#13b713',
  no: '#c04040',
  pending: '#cdcdcd'
}

function colorFor (interest) {
  if (interest === 1) return { color: COLORS.yes, name: 'green' }
  if (interest === 0) return { color: COLORS.no, name: 'red' }
  return { color: COLORS.pending, name: 'gray' }
}

export default {
  name: 'ChainDiagram',
  props: {
    chain: {
      type: Object,
      required: true
    },
    labelMap: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      markerDefs: [
        { id: 'arrowhead-gray', color: COLORS.pending },
        { id: 'arrowhead-green', color: COLORS.yes },
        { id: 'arrowhead-red', color: COLORS.no }
      ]
    }
  },
  computed: {
    supported () {
      return this.chain.swap_size === 2 || this.chain.swap_size === 3
    },
    viewBox () {
      return this.chain.swap_size === 2 ? '0 0 540 140' : '0 0 540 320'
    },
    edgeInterest () {
      const map = {}
      ;(this.chain.edges || []).forEach(edge => {
        map[edge.from_ad_id + '->' + edge.to_ad_id] = edge.interest
      })
      return map
    },
    boxes () {
      const labels = this.chain.ad_ids.map(id => ({
        id,
        label: this.labelMap[id] || id
      }))

      if (this.chain.swap_size === 2) {
        const w = 220
        const h = 100
        return [
          { ...labels[0], x: 0, y: 20, w, h },
          { ...labels[1], x: 320, y: 20, w, h }
        ]
      }

      // 3-way triangle
      const w = 200
      const h = 100
      return [
        { ...labels[0], x: 0, y: 0, w, h },
        { ...labels[1], x: 340, y: 0, w, h },
        { ...labels[2], x: 170, y: 220, w, h }
      ]
    },
    arrows () {
      if (this.chain.swap_size === 2) {
        const a = this.chain.ad_ids[0]
        const b = this.chain.ad_ids[1]
        const ab = colorFor(this.edgeInterest[a + '->' + b])
        const ba = colorFor(this.edgeInterest[b + '->' + a])
        return [
          // A → B, top
          { x1: 220, y1: 55, x2: 315, y2: 55, color: ab.color, markerId: 'arrowhead-' + ab.name },
          // B → A, bottom
          { x1: 320, y1: 85, x2: 225, y2: 85, color: ba.color, markerId: 'arrowhead-' + ba.name }
        ]
      }

      // 3-way triangle: A→B, B→C, C→A
      const a = this.chain.ad_ids[0]
      const b = this.chain.ad_ids[1]
      const c = this.chain.ad_ids[2]
      const ab = colorFor(this.edgeInterest[a + '->' + b])
      const bc = colorFor(this.edgeInterest[b + '->' + c])
      const ca = colorFor(this.edgeInterest[c + '->' + a])

      return [
        // A → B: horizontal across the top
        { x1: 200, y1: 50, x2: 335, y2: 50, color: ab.color, markerId: 'arrowhead-' + ab.name },
        // B → C: from bottom-left of B down to top-right of C
        { x1: 380, y1: 100, x2: 310, y2: 215, color: bc.color, markerId: 'arrowhead-' + bc.name },
        // C → A: from top-left of C up to bottom-right of A
        { x1: 210, y1: 215, x2: 130, y2: 100, color: ca.color, markerId: 'arrowhead-' + ca.name }
      ]
    }
  },
  methods: {
    sizeLabel (size) {
      return swapSizeLabel(size)
    }
  }
}
</script>

<style scoped>
.chain-diagram {
  width: 100%;
  margin: 0 auto;
}

.chain-svg {
  width: 100%;
  height: auto;
  display: block;
  max-width: 560px;
  margin: 0 auto;
}

.chain-box {
  fill: #fff;
  stroke: #d4d4d4;
  stroke-width: 1.5;
}

.chain-box-inner {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  box-sizing: border-box;
  text-align: center;
  font-family: inherit;
  font-size: 0.9rem;
  font-weight: 600;
  color: #222;
  line-height: 1.25;
  overflow: hidden;
}

.chain-box-label {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.chain-diagram__fallback {
  padding: 12px;
  color: #666;
  font-size: 0.9rem;
  text-align: center;
}
</style>
